<template>
    <SbsResponsive
        v-bind="containerData"
        ref="container"
        v-intersect.once="intersectOptions"
        class="sbs-image">
        <!-- @slot Слот для прелоадера во время загрузки изображения -->
        <slot
            v-if="showPreloader"
            name="preloader"
            v-bind="{ isPreloaderVisible }">
            <transition name="fade">
                <div
                    v-if="isPreloaderVisible"
                    class="sbs-image__preloader trs-slow"
                    :class="preloaderClasses">
                    <SbsSpinner
                        class="sbs-image__spinner"
                        :size="preloaderSize" />
                </div>
            </transition>
        </slot>

        <!-- @slot Слот для заглушки в случае ошибки загрузки изображения -->
        <slot
            v-if="showStub"
            name="stub"
            v-bind="{ isStubVisible }">
            <div
                v-if="isStubVisible"
                class="sbs-image__stub-wrapper">
                <div class="sbs-image__stub">
                    <SbsImageStubIcon class="sbs-image__stub-icon" />
                </div>
            </div>
        </slot>

        <!-- @slot Слот по-умолчанию для изображения -->
        <slot>
            <img
                v-bind="imageData"
                v-show="!isLoading && !hasError"
                class="sbs-image__image"
                @load="imageLoadHandler"
                @error="imageErrorHandler">
        </slot>

        <template v-if="fullscreen && !isLoading && !hasError">
            <SbsBaseButton
                v-bind="fullscreenControlData"
                class="sbs-image__fullscreen-control trs"
                @click="toggleFullscreen">
                <!-- @slot Слот для иконки полноэкранного просмотра -->
                <slot name="fullscreenControl">
                    <SbsIcon
                        v-bind="fullscreenIconData"
                        class="sbs-image__fullscreen-icon trs" />
                </slot>
            </SbsBaseButton>

            <SbsModal
                v-bind="fullscreenModalData"
                v-model="isFullscreenModalVisible">
                <!-- @slot Слот для изображения в режиме полноэкранного просмотра -->
                <slot name="fullscreenBody">
                    <img
                        v-bind="fullscreenImageData"
                        class="sbs-image__image sbs-image__image--fullscreen">
                </slot>
            </SbsModal>
        </template>
    </SbsResponsive>
</template>

<script>
import objectFitImages from 'object-fit-images';
import { AllSizes } from '../../utils/sizes.js';
import { AllColors } from '../../utils/colors.js';
import ResizeObserver from '../../utils/resizeObserver.js';
import dimensionsMixin from '../../mixins/dimensions.js';
import themesMixin from '../../mixins/themes.js';
import SbsModal from '../modal/SbsModal.vue';
import SbsBaseButton from '../button/SbsBaseButton.vue';
import SbsImageStubIcon from './SbsImageStubIcon.vue';

/**
 * Компонент, предназначенный для отображения изображений
 */
export default {
    name: 'SbsImage',
    components: {
        SbsImageStubIcon,
        SbsModal,
        SbsBaseButton,
    },
    mixins: [dimensionsMixin, themesMixin],
    data() {
        return {
            isFullscreenModalVisible: false,
            isLoading: true,
            hasError: false,
            computedSrc: '',
            containerBoundaries: {
                width: null,
                height: null,
            },
            resizeObserver: null,
        };
    },
    computed: {
        fullscreenControlData() {
            return {
                class: this.fullscreenControlClass,
            };
        },
        fullscreenModalData() {
            return {
                height: '100%',
                width: '100%',
                modalContentClass: `sbs-image__fullscreen-container ${this.fullscreenContainerClass}`,
                modalBodyClass: `sbs-image__fullscreen-body ${this.fullscreenBodyClass}`,
                showOverlay: false,
            };
        },
        preloaderClasses() {
            const classes = [];

            if (this.preloaderBackgroundTheme) {
                classes.push(`bg-${this.preloaderBackgroundTheme}`);
            }

            if (this.preloaderBorderTheme) {
                classes.push(`bdc-${this.preloaderBorderTheme}`);
            }

            if (this.preloaderColorTheme) {
                classes.push(`clr-${this.preloaderColorTheme}`);
            }

            if (this.preloaderBordered) {
                classes.push('bordered');
            }

            return [
                ...classes,
                this.preloaderClass,
            ];
        },
        intersectOptions() {
            return {
                enabled: this.lazyLoading,
                callback: (_entries, _observer, isIntersecting) => {
                    if (isIntersecting) {
                        this.loadImage();
                    }
                },
                options: this.lazyLoadingOptions,
            };
        },
        computedAspectRatio() {
            return Number(this.aspectRatio);
        },
        containerData() {
            const data = {
                ...this.dimensionData,
                class: this.themeClasses,
            };

            if (this.computedAspectRatio) {
                data.aspectRatio = this.computedAspectRatio;
                data.width = this.width || this.containerBoundaries.width;
                data.height = this.height || this.containerBoundaries.height;
            }

            return data;
        },
        isPreloaderVisible() {
            return Boolean((
                this.loading ||
                (
                    this.src &&
                    this.isLoading &&
                    !this.hasError
                )
            ));
        },
        isStubVisible() {
            return Boolean((
                !this.src ||
                (
                    !this.isLoading &&
                    this.hasError
                )
            ));
        },
        defaultImageData() {
            const data = {
                alt: this.alt,
                class: [this.imageClass],
            };

            if (this.computedSrc) {
                data.src = this.computedSrc;
            }

            return data;
        },
        imageData() {
            return {
                ...this.defaultImageData,
                class: [
                    ...this.defaultImageData.class,
                    { cover: this.cover },
                ],
            };
        },
        fullscreenImageData() {
            return {
                ...this.defaultImageData,
                class: [
                    ...this.defaultImageData.class,
                    { fullscreen: this.fullscreen },
                ],
            };
        },
        fullscreenIconData() {
            return {
                backgroundTheme: AllColors.LIGHTBLUE,
                iconSize: AllSizes.X_LARGE,
                iconName: 'magnifying-glass-plus',
            };
        },
    },
    methods: {
        calcContainerBoundaries() {
            const { width } = this.$refs.container.$el.getBoundingClientRect();

            if (!this.width && this.height && this.computedAspectRatio) {
                this.containerBoundaries.width = this.height * this.computedAspectRatio;
                this.containerBoundaries.height = this.height;
            } else {
                this.containerBoundaries.width = width;
                this.containerBoundaries.height = width / this.computedAspectRatio;
            }
        },
        loadImage() {
            this.computedSrc = this.src;
        },
        imageLoadHandler(e) {
            this.isLoading = false;
            this.hasError = false;
            /**
             * Эмит события `load`, срабатывает когда src изображения валидно и загружено
             */
            this.$emit('load', e);
        },
        imageErrorHandler(e) {
            this.isLoading = false;
            this.hasError = true;
            /**
             * Эмит события `error`, срабатывает при неправильном src изображения
             */
            this.$emit('error', e);
        },
        showFullscreen() {
            this.isFullscreenModalVisible = true;
        },
        hideFullscreen() {
            this.isFullscreenModalVisible = false;
        },
        toggleFullscreen() {
            this.isFullscreenModalVisible
                ? this.hideFullscreen()
                : this.showFullscreen();
        },
    },
    props: {
        /**
         * Значение атрибута `alt` тега `<img>`
         */
        alt: {
            type: String,
            required: true,
        },
        /**
         * Коэффициент соотношения сторон изображения
         * Например, 1.77 примерно равен соотношению 16:9
         */
        aspectRatio: {
            type: [Number, String],
            default: null,
        },
        /**
         * Заполняет изображением весь контейнер
         */
        cover: {
            type: Boolean,
            default: false,
        },
        /**
         * Включает возможность показа изображения на весь экран
         */
        fullscreen: {
            type: Boolean,
            default: false,
        },
        /**
         * Класс контента фуллскрина
         */
        fullscreenBodyClass: {
            type: String,
            default: '',
        },
        /**
         * Класс контейнера фуллскрина
         */
        fullscreenContainerClass: {
            type: String,
            default: '',
        },
        /**
         * Класс кнопки активации фуллскрина
         */
        fullscreenControlClass: {
            type: String,
            default: '',
        },
        /**
         * Имя класса для тега `<img>`
         */
        imageClass: {
            type: String,
            default: '',
        },
        /**
         * "Ленивая" загрузка - изображение будет загружено только в момент появления во вьюпорте (настраивается с помощью lazyLoadingOptions)
         */
        lazyLoading: {
            type: Boolean,
            default: true,
        },
        /**
         * Настройки "ленивой" загрузки изображения
         */
        lazyLoadingOptions: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Включает отображение прелоадера, если `showPreloader: true`
         */
        loading: {
            type: Boolean,
            default: false,
        },
        /**
         * Тема (цветовые решения) для цвета фона прелоадера
         */
        preloaderBackgroundTheme: {
            type: String,
            default: AllColors.LIGHTBLUE,
        },
        /**
         * Определяет наличие бордера у прелоадера
         */
        preloaderBordered: {
            type: Boolean,
            default: true,
        },
        /**
         * Тема (цветовые решения) для цвета бордера прелоадера
         */
        preloaderBorderTheme: {
            type: String,
            default: AllColors.PRIMARY,
        },
        /**
         * Класс для прелоадера
         */
        preloaderClass: {
            type: String,
            default: '',
        },
        /**
         * Тема (цветовые решения) для цвета прелоадера
         */
        preloaderColorTheme: {
            type: String,
            default: AllColors.PRIMARY,
        },
        /**
         * Устанавливает размер спиннера для прелоадера
         */
        preloaderSize: {
            type: String,
            default: AllSizes.MEDIUM,
        },
        /**
         * Определяет видимость прелоадера загрузки изображения
         */
        showPreloader: {
            type: Boolean,
            default: true,
        },
        /**
         * Определяет видимость заглушки в случае ошибки загрузки изображения
         */
        showStub: {
            type: Boolean,
            default: true,
        },
        /**
         * Ссылка на изображение для тега `<img>`
         */
        src: {
            type: String,
            default: '',
        },
    },
    watch: {
        src(value) {
            this.isLoading = true;
            this.hasError = false;
            this.computedSrc = value;
        },
    },
    created() {
        this.resizeObserver = new ResizeObserver(this.calcContainerBoundaries);
    },
    async mounted() {
        objectFitImages();

        if (this.$el) {
            this.resizeObserver.observe(this.$el);
        }

        await this.$nextTick();

        this.calcContainerBoundaries();
    },
    beforeUnmount() {
        this.resizeObserver.disconnect();
    },
};
</script>

<style lang="scss">
.sbs-image {
    position: relative;
    max-width: 100%;
    max-height: 100%;

    &__preloader,
    &__stub-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    }

    &__spinner {
        max-width: 50%;
        max-height: 50%;
    }

    &__stub {
        display: flex;
        width: 60px;
        max-width: 50%;
        height: 60px;
        max-height: 50%;
        margin: auto;
    }

    &__stub-icon {
        width: 100%;
        height: 100%;
    }

    &__picture,
    &__image {
        display: flex;
        width: 100%;
        height: 100%;
    }

    &__image {
        object-fit: contain;
        // stylelint-disable-next-line font-family-no-missing-generic-family-keyword
        font-family: "object-fit: contain;";

        &.cover {
            object-fit: cover;
            // stylelint-disable-next-line font-family-no-missing-generic-family-keyword
            font-family: "object-fit: cover;";
        }

        &.fullscreen {
            background-color: $black;
            border-radius: $rounded-15;
        }
    }

    &__fullscreen-control {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    &__fullscreen-icon {
        padding: 10px;
        border-radius: $rounded-circle;
        transform: scale(0);
        opacity: 0;

        @include has-hover {
            .sbs-image__fullscreen-control:hover & {
                transform: scale(1);
                opacity: 0.5;
            }
        }

        .sbs-image__fullscreen-control:focus & {
            transform: scale(1);
            opacity: 0.5;
        }
    }

    &__fullscreen-container {
        border-radius: $rounded-0;
    }

    &__fullscreen-body {
        background-color: rgba($black, 0.85);
    }
}
</style>
